import type { LotView, Outcome } from "@hammer/core";
import { parseMoney } from "@hammer/core/money";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import { keys } from "../lib/queries.ts";
import { toast } from "../lib/store.ts";

const OUTCOMES: [Outcome, string][] = [
  ["won", "I won"],
  ["lost", "Someone else won"],
  ["no_sale", "No sale"],
  ["unknown", "Don't know"],
];

/** Record how a lot ended. Sanity-checks the price against the last bid seen. */
export function ResultForm({ lot, onDone }: { lot: LotView; onDone?: () => void }) {
  const qc = useQueryClient();
  const suggested = lot.result?.hammerCents ?? lot.latest?.soldPriceCents ?? lot.latest?.currentBidCents ?? null;
  const [outcome, setOutcome] = useState<Outcome | null>(lot.result?.recordedBy === "user" ? lot.result.outcome : null);
  const [price, setPrice] = useState(suggested != null ? String(suggested / 100) : "");
  const [warning, setWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cents = price.trim() ? parseMoney(price) : null;
  const priceInvalid = price.trim() !== "" && cents == null;

  async function save(confirmed = false) {
    if (!outcome || priceInvalid) return;
    setBusy(true);
    try {
      await api(`/lots/${lot.id}/result`, { method: "PUT", body: { outcome, hammerCents: outcome === "no_sale" ? null : cents, confirmed } });
      setWarning(null);
      toast("Result recorded");
      await Promise.all([qc.invalidateQueries({ queryKey: keys.lots }), qc.invalidateQueries({ queryKey: keys.issues }), qc.invalidateQueries({ queryKey: keys.lot(lot.id) })]);
      onDone?.();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setWarning((e.data?.warning as string) ?? "That price looks unusual.");
      else toast(`Couldn't save: ${(e as Error).message}`, { tone: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-3" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="How did it end?">
        {OUTCOMES.map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={outcome === v} className={`btn ${outcome === v ? "btn-primary" : ""}`} onClick={() => setOutcome(v)}>{label}</button>
        ))}
      </div>
      {outcome !== "no_sale" && (
        <div className="flex items-end gap-2 flex-wrap">
          <label className="grid">
            <span className="label">Sold for</span>
            <input className="field num w-40" inputMode="decimal" value={price} onChange={(e) => { setPrice(e.target.value); setWarning(null); }} placeholder="$0" aria-invalid={priceInvalid} />
          </label>
          <button className="btn btn-primary" disabled={!outcome || busy || priceInvalid}>Save result</button>
        </div>
      )}
      {outcome === "no_sale" && <div><button className="btn btn-primary" disabled={busy}>Save result</button></div>}
      {priceInvalid && <p className="text-red text-[13.5px]">Enter an amount like 3800 or $3,800.</p>}
      {warning && (
        <div className="flex items-center gap-3 flex-wrap text-[14px]">
          <span className="text-amber">{warning} Is that right?</span>
          <button type="button" className="btn" onClick={() => void save(true)} disabled={busy}>Yes, save it</button>
        </div>
      )}
    </form>
  );
}
